import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { formatUsd } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MessageSquarePlus, Play, CheckCircle2, Pencil, Sparkles, FileText, Circle,
  CheckCircle, CircleDot, Link2, Unlink, RefreshCw,
} from "lucide-react";

const STAGE_STATUS = ["NOT_STARTED", "IN_PROGRESS", "COMPLETED", "SKIPPED"];

function StageIcon({ status }) {
  if (status === "COMPLETED") return <CheckCircle className="h-5 w-5 text-green-600" />;
  if (status === "IN_PROGRESS") return <CircleDot className="h-5 w-5 text-orange-500" />;
  if (status === "SKIPPED") return <Circle className="h-5 w-5 text-slate-300" />;
  return <Circle className="h-5 w-5 text-gray-300" />;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = ["ADMIN", "MANAGER", "COORDINATOR"].includes(user?.role);
  // POST/DELETE /groups is ADMIN+MANAGER only — coordinators must not see the controls.
  const canMapGroups = ["ADMIN", "MANAGER"].includes(user?.role);
  const [project, setProject] = useState(null);
  const [editStage, setEditStage] = useState(null);
  const [groupJid, setGroupJid] = useState("");
  const [groupSubject, setGroupSubject] = useState("");
  const [availableGroups, setAvailableGroups] = useState([]);
  const [pickedJid, setPickedJid] = useState("");
  const [manualEntry, setManualEntry] = useState(false);
  const [groupsBusy, setGroupsBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [classifying, setClassifying] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [summaryDate, setSummaryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busyStage, setBusyStage] = useState(null);

  const loadAvailableGroups = useCallback(() => {
    api.get("/whatsapp/available-groups")
      .then((r) => setAvailableGroups(r.data))
      .catch(() => setAvailableGroups([]));
  }, []);

  const load = useCallback(() => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data));
    api.get(`/messages?project_id=${id}`).then((r) => setMessages(r.data));
    api.get(`/summaries?project_id=${id}`).then((r) => setSummaries(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAvailableGroups(); }, [loadAvailableGroups]);

  const unmappedGroups = availableGroups.filter((g) => !g.mapped_project_id);

  const handleFlow = (data) => {
    if (data?.flow?.auto_started_stage) {
      toast.info(`Next stage started: ${data.flow.auto_started_stage}`);
    }
    if (data?.flow?.project_status === "COMPLETED") {
      toast.success("All stages done — project marked COMPLETED 🎉");
    } else if (data?.flow?.project_status === "ACTIVE") {
      toast.info("Project is now ACTIVE");
    }
  };

  const setSubstageStatus = async (substage, status) => {
    setBusyStage(substage.id);
    try {
      const { data } = await api.put(`/project-substages/${substage.id}`, { status });
      toast.success(status === "COMPLETED" ? `${substage.substage_name} completed` : `${substage.substage_name} started`);
      handleFlow(data);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyStage(null); }
  };

  const saveStage = async () => {
    try {
      const { data } = await api.put(`/project-substages/${editStage.id}`, {
        status: editStage.status,
        probable_end_date: editStage.probable_end_date || null,
        notes: editStage.notes || null,
      });
      toast.success("Substage updated");
      handleFlow(data);
      setEditStage(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const mapGroup = async (group_jid, group_subject) => {
    if (!group_jid) return;
    setGroupsBusy(true);
    try {
      await api.post("/groups", { project_id: id, group_jid, group_subject: group_subject || "" });
      toast.success("Group mapped to this project");
      setGroupJid(""); setGroupSubject(""); setPickedJid("");
      load();
      loadAvailableGroups();
    } catch (e) { toast.error(apiError(e)); }
    finally { setGroupsBusy(false); }
  };

  const addGroup = () => mapGroup(groupJid.trim(), groupSubject.trim());

  const mapPickedGroup = () => {
    const g = availableGroups.find((x) => x.jid === pickedJid);
    if (g) mapGroup(g.jid, g.subject);
  };

  const unmapGroup = async (group) => {
    if (!window.confirm(`Unlink "${group.group_subject || group.group_jid}" from this project? New messages from it will stop being captured.`)) return;
    setGroupsBusy(true);
    try {
      await api.delete(`/groups/${group.id}`);
      toast.success("Group unlinked");
      load();
      loadAvailableGroups();
    } catch (e) { toast.error(apiError(e)); }
    finally { setGroupsBusy(false); }
  };

  const classify = async () => {
    setClassifying(true);
    try {
      const { data } = await api.post(`/messages/classify?project_id=${id}`);
      toast.success(`${data.classified} messages classified by AI`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setClassifying(false); }
  };

  const generateSummary = async () => {
    setGenerating(true);
    try {
      await api.post(`/summaries/generate?project_id=${id}&date=${summaryDate}`);
      toast.success("Summary generated");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };

  if (!project) return <div className="text-muted-foreground">Loading…</div>;

  const stages = project.stages || [];
  const progressPct = project.progress_pct ?? 0;
  const allSubstages = stages.flatMap((s) => (s.substages || []).map((sub) => ({ ...sub, stageName: s.stage_name })));
  const current = allSubstages.find((s) => s.status === "IN_PROGRESS");
  const anyInProgress = Boolean(current);
  const nextStartable = !anyInProgress ? allSubstages.find((s) => s.status === "NOT_STARTED") : null;
  const completedCount = stages.filter((s) => s.status === "COMPLETED").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">{project.name}</h1>
            <StatusBadge status={project.status} />
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            <span className="font-mono">{project.code}</span> · {project.client?.name}
            {project.ai_cost_usd !== undefined && (
              <> · <Link to={`/settings/ai-usage?project_id=${project.id}`} className="hover:text-foreground underline-offset-2 hover:underline"
                data-testid="project-ai-cost">AI cost: {formatUsd(project.ai_cost_usd)}</Link></>
            )}
          </div>
          {project.site_address && <div className="text-sm text-muted-foreground">{project.site_address}</div>}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">
              {completedCount}/{stages.length} stages done
              {current && <span className="text-muted-foreground font-normal"> · currently: {current.stageName} — {current.substage_name}</span>}
              {!current && nextStartable && <span className="text-muted-foreground font-normal"> · next: {nextStartable.stageName} — {nextStartable.substage_name}</span>}
              {!current && !nextStartable && stages.length > 0 && <span className="text-green-600 font-normal"> · all stages complete</span>}
            </span>
            <span className="text-muted-foreground">{progressPct}%</span>
          </div>
          <Progress value={progressPct} className="h-2" data-testid="project-progress" />
        </CardContent>
      </Card>

      <Tabs defaultValue="stages">
        <TabsList>
          <TabsTrigger value="stages" data-testid="tab-stages">Stages</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages ({messages.length})</TabsTrigger>
          <TabsTrigger value="updates" data-testid="tab-updates">Work Updates</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups">WhatsApp Groups</TabsTrigger>
        </TabsList>

        <TabsContent value="stages" className="mt-4">
          <div className="relative">
            {stages.map((s) => (
              <div key={s.id} className="mb-4" data-testid={`stage-row-${s.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-heading font-semibold flex items-center gap-2">
                    <span className="text-xs font-mono text-muted-foreground">{s.sequence_no}.</span>
                    {s.stage_name}
                    <span className="text-xs text-muted-foreground font-normal">({s.weight_pct}%)</span>
                  </div>
                  <StatusBadge status={s.status} />
                </div>
                <div className="ml-6 space-y-2">
                  {(s.substages || []).map((sub) => (
                    <div key={sub.id} className="flex gap-3" data-testid={`substage-row-${sub.id}`}>
                      <StageIcon status={sub.status} />
                      <Card className={`flex-1 ${sub.status === "IN_PROGRESS" ? "border-orange-300 shadow-sm" : ""}`}>
                        <CardContent className="p-3 flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <div className="font-medium text-sm">{sub.substage_name} <span className="text-xs text-muted-foreground font-normal">({sub.weight_pct}%)</span></div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {sub.actual_start_date && `Started: ${sub.actual_start_date}`}
                              {sub.probable_end_date && ` · Target: ${sub.probable_end_date}`}
                              {sub.actual_end_date && ` · Done: ${sub.actual_end_date}`}
                              {!sub.actual_start_date && !sub.probable_end_date && !sub.actual_end_date && "Not started"}
                            </div>
                            {sub.notes && <div className="text-xs text-muted-foreground italic mt-0.5">{sub.notes}</div>}
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusBadge status={sub.status} />
                            {canEdit && sub.status === "IN_PROGRESS" && (
                              <Button size="sm" className="rounded-full" disabled={busyStage === sub.id}
                                onClick={() => setSubstageStatus(sub, "COMPLETED")} data-testid={`complete-stage-${sub.id}`}>
                                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark Completed
                              </Button>
                            )}
                            {canEdit && nextStartable?.id === sub.id && (
                              <Button size="sm" variant="outline" className="rounded-full" disabled={busyStage === sub.id}
                                onClick={() => setSubstageStatus(sub, "IN_PROGRESS")} data-testid={`start-stage-${sub.id}`}>
                                <Play className="h-4 w-4 mr-1.5" /> Start Stage
                              </Button>
                            )}
                            {canEdit && (
                              <Button variant="ghost" size="sm" className="rounded-full"
                                onClick={() => setEditStage({ ...sub, stage_name: sub.substage_name })} data-testid={`edit-stage-${sub.id}`}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="messages" className="space-y-3 mt-4">
          {canEdit && (
            <div className="flex justify-end">
              <Button size="sm" variant="outline" className="rounded-full" onClick={classify} disabled={classifying}>
                <Sparkles className="h-4 w-4 mr-1.5" /> {classifying ? "Classifying…" : "Classify with AI"}
              </Button>
            </div>
          )}
          {messages.length === 0 && (
            <p className="text-sm text-muted-foreground">No messages yet. Map a WhatsApp group to start capturing site updates.</p>
          )}
          {messages.map((m) => (
            <Card key={m.id}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-sm font-medium">{m.sender_name}</span>
                  <div className="flex items-center gap-2">
                    {m.classification && (
                      <Badge variant="outline" className="text-[10px]">
                        {m.classification.category?.replace(/_/g, " ")}
                        {m.classification.stage_name && ` · ${m.classification.stage_name}`}
                      </Badge>
                    )}
                    <span className="text-[11px] text-muted-foreground">
                      {new Date(m.sent_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                </div>
                <p className="text-sm mt-1 whitespace-pre-wrap">{m.body}</p>
                {m.media_text && (
                  <p className="text-xs text-muted-foreground italic mt-1 flex items-start gap-1">
                    <Sparkles className="h-3 w-3 shrink-0 mt-0.5" /> {m.media_text}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="updates" className="space-y-3 mt-4">
          {canEdit && (
            <Card><CardContent className="p-4 flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label>Date</Label>
                <Input type="date" value={summaryDate} onChange={(e) => setSummaryDate(e.target.value)} className="w-44" data-testid="summary-date-input" />
              </div>
              <Button onClick={generateSummary} disabled={generating} className="rounded-full" data-testid="generate-summary-button">
                <FileText className="h-4 w-4 mr-1.5" /> {generating ? "Generating…" : "Generate Summary"}
              </Button>
            </CardContent></Card>
          )}
          {summaries.length === 0 && (
            <p className="text-sm text-muted-foreground">No work updates yet. Generate a daily summary from the captured messages.</p>
          )}
          {summaries.map((s) => (
            <Card key={s.id}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-medium text-sm">{s.summary_date}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">{s.message_count} messages</span>
                    <StatusBadge status={s.status} />
                  </div>
                </div>
                {s.internal_text && (
                  <div>
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Internal</p>
                    <p className="text-sm whitespace-pre-wrap">{s.internal_text}</p>
                  </div>
                )}
                {(s.edited_client_text || s.client_text) && (
                  <div>
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Client update</p>
                    <p className="text-sm whitespace-pre-wrap">{s.edited_client_text || s.client_text}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="groups" className="space-y-3 mt-4">
          {canMapGroups && (
            <Card><CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-medium flex items-center gap-2">
                  <MessageSquarePlus className="h-4 w-4" /> Map a WhatsApp group
                </div>
                <Button variant="ghost" size="sm" className="rounded-full h-8"
                  onClick={loadAvailableGroups} data-testid="refresh-available-groups">
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh list
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Only mapped groups are captured and shown on the WhatsApp page. Pick a group the
                bridge has discovered, or enter its ID manually if the bridge is offline.
              </p>

              <div className="flex flex-wrap items-end gap-2">
                <div className="space-y-1 min-w-[16rem] flex-1">
                  <Label>WhatsApp group</Label>
                  <Select value={pickedJid} onValueChange={setPickedJid}>
                    <SelectTrigger data-testid="group-picker-select">
                      <SelectValue placeholder={unmappedGroups.length ? "Select a group…" : "No unmapped groups available"} />
                    </SelectTrigger>
                    <SelectContent>
                      {unmappedGroups.map((g) => (
                        <SelectItem key={g.jid} value={g.jid}>
                          {g.subject}{g.participants ? ` · ${g.participants} participants` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={mapPickedGroup} disabled={!pickedJid || groupsBusy}
                  className="rounded-full" data-testid="map-picked-group-button">
                  <Link2 className="h-3.5 w-3.5 mr-1.5" /> Map Group
                </Button>
              </div>

              {availableGroups.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No groups discovered yet — link WhatsApp on the WhatsApp page, then refresh.
                </p>
              )}
              {availableGroups.length > 0 && unmappedGroups.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Every discovered group is already mapped to a project.
                </p>
              )}

              <button type="button" className="text-xs text-primary underline underline-offset-2"
                onClick={() => setManualEntry((v) => !v)} data-testid="toggle-manual-group-entry">
                {manualEntry ? "Hide manual entry" : "Enter a group ID manually"}
              </button>
              {manualEntry && (
                <div className="space-y-3 border-t border-border pt-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1"><Label>Group JID</Label>
                      <Input value={groupJid} onChange={(e) => setGroupJid(e.target.value)} placeholder="1203...@g.us" data-testid="group-jid-input" /></div>
                    <div className="space-y-1"><Label>Group Name</Label>
                      <Input value={groupSubject} onChange={(e) => setGroupSubject(e.target.value)} placeholder="Site — Andheri Flat" data-testid="group-subject-input" /></div>
                  </div>
                  <Button onClick={addGroup} disabled={!groupJid.trim() || groupsBusy}
                    className="rounded-full" data-testid="add-group-button">Map Group</Button>
                </div>
              )}
            </CardContent></Card>
          )}

          {project.groups.length === 0 ? (
            <p className="text-sm text-muted-foreground">No groups mapped to this project yet.</p>
          ) : project.groups.map((g) => (
            <Card key={g.id}><CardContent className="p-4 flex justify-between items-center gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{g.group_subject || "Unnamed group"}</div>
                <div className="text-xs font-mono text-muted-foreground truncate">{g.group_jid}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status={g.is_listening ? "ACTIVE" : "ON_HOLD"} />
                {canMapGroups && (
                  <Button size="sm" variant="outline" className="rounded-full" disabled={groupsBusy}
                    onClick={() => unmapGroup(g)} data-testid={`unmap-group-${g.id}`}>
                    <Unlink className="h-3.5 w-3.5 mr-1.5" /> Unlink
                  </Button>
                )}
              </div>
            </CardContent></Card>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!editStage} onOpenChange={(o) => !o && setEditStage(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editStage?.stage_name}</DialogTitle></DialogHeader>
          {editStage && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>Status</Label>
                <Select value={editStage.status} onValueChange={(v) => setEditStage({ ...editStage, status: v })}>
                  <SelectTrigger data-testid="stage-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGE_STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label>Probable End Date</Label>
                <Input type="date" value={editStage.probable_end_date || ""} onChange={(e) => setEditStage({ ...editStage, probable_end_date: e.target.value })} data-testid="stage-date-input" /></div>
              <div className="space-y-1"><Label>Notes</Label>
                <Input value={editStage.notes || ""} onChange={(e) => setEditStage({ ...editStage, notes: e.target.value })} data-testid="stage-notes-input" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveStage} data-testid="save-stage-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
